import type { ReactNode } from 'react';
import { useReducedMotion } from 'motion/react';
import { cn } from '@/lib/utils';

// Wraps a mark (the idle_smooth gif) with a charter-style rotating + breathing
// gold ring and a soft radial glow. size = the content box in px. Honors
// prefers-reduced-motion by dropping the perpetual animations (the ring + glow
// stay visible, just static).
type Props = { children: ReactNode; size?: number; className?: string };

export function GlowRing({ children, size = 120, className }: Props) {
  const reduce = useReducedMotion();
  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      {/* breathing radial glow — sized larger than the avatar so it reads as a halo */}
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          inset: '-24%',
          background: 'radial-gradient(circle, rgba(212,175,55,.4), transparent 70%)',
          animation: reduce ? undefined : 'ringBreath 4s ease-in-out infinite',
        }}
      />
      {/* rotating conic ring hugging the avatar */}
      <span
        aria-hidden
        className="absolute rounded-full"
        style={{
          inset: -3,
          padding: 2,
          animation: reduce ? undefined : 'ringRotate 12s linear infinite',
          background:
            'conic-gradient(from 0deg, transparent, var(--accent), var(--gold-soft), transparent 60%)',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      {/* circular avatar — the child (gif) fills and is center-cropped via object-cover */}
      <div
        className="relative z-10 overflow-hidden rounded-full"
        style={{ width: size, height: size }}
      >
        {children}
      </div>
    </div>
  );
}
